import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme, THEMES } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { loadOpsConfig, saveOpsConfig, DEFAULT_OPS_CONFIG } from '../config/opsConfig';

/* Fonts from the app design system (App.css): body = Hanken Grotesk, label = JetBrains Mono. */
const FONT_BODY = 'var(--font-body)';
const FONT_MONO = 'var(--font-label)';
// The desktop app sets window.__APP_VERSION__ (DESKTOP-UTMS setup.ts); the web app has no release number.
const APP_VERSION = window.__APP_VERSION__ ? `Desktop v${window.__APP_VERSION__}` : 'Web';

/* Data-grid styling for the param tables (zebra, hover, gridlines, custom number stepper). */
const PARAM_CSS = `
.mpcfg{width:100%;border-collapse:collapse;table-layout:fixed;font-family:var(--font-label);}
/* App.css gives every button margin-bottom 8px, 8px corners and a hover lift; none of it belongs in a grid row. */
.mpcfg button{margin:0;border-radius:0;}
.mpcfg-page button:hover{transform:none;}
.mpcfg th{text-align:left;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--textMuted);padding:7px 12px;background:var(--bgTertiary);border-bottom:1px solid var(--borderSecondary);white-space:nowrap;position:sticky;top:0;}
.mpcfg th.th-def{text-align:right;}
.mpcfg th.th-unit{text-align:center;}
.mpcfg th.th-act{text-align:center;}
.mpcfg td{padding:6px 14px;height:44px;border-bottom:1px solid var(--borderSecondary);font-size:12.5px;vertical-align:middle;}
.mpcfg tbody tr{transition:background .12s;}
.mpcfg tbody tr:nth-child(even){background:color-mix(in srgb, var(--textMuted) 5%, transparent);}
.mpcfg tbody tr.mod{background:color-mix(in srgb, var(--accent) 7%, transparent);}
.mpcfg tbody tr:hover{background:color-mix(in srgb, var(--accent) 6%, transparent);}
.mpcfg tbody tr.mod:hover{background:color-mix(in srgb, var(--accent) 11%, transparent);}
.mpcfg .c-name{position:relative;padding-left:28px;font-weight:600;color:var(--textPrimary);white-space:normal;line-height:1.35;}
.mpcfg .c-name .dot{position:absolute;left:13px;top:50%;width:6px;height:6px;margin-top:-3px;border-radius:50%;background:transparent;}
.mpcfg .c-name .dot.on{background:var(--accent);box-shadow:0 0 5px var(--accent);}
.mpcfg .c-def{text-align:right;color:var(--textMuted);font-variant-numeric:tabular-nums;white-space:nowrap;}
.mpcfg .c-unit{text-align:center;font-size:11px;color:var(--textMuted);white-space:nowrap;}
.mpcfg .c-desc{white-space:normal;font-family:var(--font-body);font-size:11.5px;color:var(--textMuted);line-height:1.4;}
.mpcfg .c-act{text-align:center;}
.mpcfg .rst{border:none;background:transparent;cursor:pointer;color:var(--accent);padding:4px;line-height:0;border-radius:4px;}
.mpcfg .rst:hover:not(:disabled){background:color-mix(in srgb, var(--accent) 15%, transparent);}
.mpcfg .rst:disabled{visibility:hidden;}
.mpcfg-numwrap{display:flex;align-items:stretch;width:100%;height:28px;border:1px solid var(--borderSecondary);border-radius:5px;overflow:hidden;background:var(--bgPrimary);}
.mpcfg-numwrap:focus-within{border-color:var(--accent);box-shadow:0 0 0 2px color-mix(in srgb, var(--accent) 18%, transparent);}
.mpcfg-num{flex:1;width:auto;min-width:0;border:none;background:transparent;color:var(--textPrimary);font-family:var(--font-label);font-size:13px;font-weight:600;text-align:right;padding:0 10px;margin:0;height:100%;border-radius:0;backdrop-filter:none;outline:none;-moz-appearance:textfield;}
/* App.css styles every input (12px bottom margin, focus glow); undo it inside the stepper. */
.mpcfg-num:focus{border:none;background:transparent;box-shadow:none;}
.mpcfg-num::-webkit-outer-spin-button,.mpcfg-num::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}
.mpcfg-step{display:flex;flex-direction:column;border-left:1px solid var(--borderSecondary);}
.mpcfg-step button{flex:1;width:20px;border:none;background:var(--bgTertiary);color:var(--textMuted);cursor:pointer;font-size:7px;line-height:1;display:flex;align-items:center;justify-content:center;padding:0;transition:background .1s;}
.mpcfg-step button:hover{background:var(--accent);color:var(--bgPrimary);}
.mpcfg-step button:first-child{border-bottom:1px solid var(--borderSecondary);}
.mpcfg-color{display:flex;width:100%;height:28px;align-items:center;gap:9px;}
.mpcfg-color input[type=color]{width:30px;height:24px;padding:0;margin:0;border:1px solid var(--borderSecondary);border-radius:4px;background:transparent;backdrop-filter:none;cursor:pointer;flex-shrink:0;}
.mpcfg-color input[type=color]::-webkit-color-swatch-wrapper{padding:2px;}
.mpcfg-color input[type=color]::-webkit-color-swatch{border:none;border-radius:2px;}
.mpcfg-hex{font-family:var(--font-label);font-size:11px;color:var(--textMuted);text-transform:uppercase;}
.mpcfg-tgl{display:flex;width:100%;height:28px;align-items:center;gap:11px;}
.mpcfg-tgllbl{font-family:var(--font-label);font-size:11px;color:var(--textMuted);text-transform:uppercase;letter-spacing:.05em;}
`;

/* ─── Section nav ─── */
const SECTIONS = [
  { id: 'appearance', label: 'Appearance', icon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z' },
  { id: 'map', label: 'Map', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' },
  { id: 'delivery', label: 'Delivery', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
  { id: 'dropTiming', label: 'Drop Timing', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'airspace', label: 'Airspace', icon: 'M3 12h4l3-9 4 18 3-9h4' },
  { id: 'safety', label: 'Safety', icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
  { id: 'tuning3d', label: '3D Tuning', icon: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12' },
  { id: 'units', label: 'Units', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  { id: 'notifications', label: 'Alerts', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
  { id: 'about', label: 'About', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'danger', label: 'Danger Zone', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
];
const iconOf = (id) => (SECTIONS.find(s => s.id === id) || {}).icon;

/* Numeric ops-config field specs. */
const OPS_FIELDS = {
  delivery: [
    { key: 'cruiseAltitude', label: 'Cruise Altitude', sub: 'Height flown between stops', unit: 'm', min: 1, max: 500, step: 1 },
    { key: 'navSpeed', label: 'Cruise Speed', sub: 'Horizontal nav speed', unit: 'm/s', min: 0.1, max: 20, step: 0.1 },
    { key: 'dropHeight', label: 'Drop Hover Height', sub: 'Descends to this AGL before the winch runs', unit: 'm', min: 1, max: 200, step: 1 },
    { key: 'ropeLength', label: 'Rope Length', sub: 'Cable paid out to reach the ground', unit: 'm', min: 1, max: 100, step: 1 },
    { key: 'winchRate', label: 'Winch Rate', sub: "Speed the winch really runs at — not higher than the autopilot's WINCH_RATE_MAX. The drop waits rope ÷ rate before the hook opens", unit: 'm/s', min: 0.1, max: 5, step: 0.1 },
    { key: 'servoChannel', label: 'Hook Servo Channel', sub: 'Release servo output channel', unit: '', min: 1, max: 16, step: 1 },
    { key: 'servoPWM', label: 'Hook Release PWM', sub: 'PWM that opens the hook', unit: 'µs', min: 800, max: 2200, step: 1 },
    { key: 'depotLat', label: 'Depot Latitude', sub: 'Centre of the customer delivery area. Leave both blank to fall back to a drone home — not recommended, it moves with the fleet', unit: '°', min: -90, max: 90, step: 0.00001 },
    { key: 'depotLon', label: 'Depot Longitude', sub: 'Centre of the customer delivery area', unit: '°', min: -180, max: 180, step: 0.00001 },
    { key: 'serviceRadiusMeters', label: 'Service Radius', sub: 'Furthest a customer may place a drop point from the depot', unit: 'm', min: 50, max: 50000, step: 50 },
    { key: 'clearanceRadiusMeters', label: 'Drop Clearance Radius', sub: 'Open-space circle the customer must keep clear', unit: 'm', min: 2, max: 100, step: 1 },
    { key: 'minSeparationMeters', label: 'Drop Point Separation', sub: 'Minimum gap between two open deliveries', unit: 'm', min: 0, max: 500, step: 1 },
    { key: 'minDescentBandMeters', label: 'Descent Band', sub: 'Minimum vertical gap kept between cruise and hover height', unit: 'm', min: 1, max: 100, step: 1 },
    { key: 'pickupTimeoutMin', label: 'Medical Pickup Alert', sub: 'Alert when a drone landed on a medical pad has waited this long for the recipient. Alert only — it never leaves on its own', unit: 'min', min: 1, max: 240, step: 1 },
  ],
  dropTiming: [
    { key: 'settleBandMeters', label: 'Arrive Band', sub: 'Must be within this of drop height to count as arrived', unit: 'm', min: 0.2, max: 10, step: 0.1 },
    { key: 'settleDescentRateMps', label: 'Settle Descent Rate', sub: 'Must descend slower than this before the drop starts (stops mid-descent drops)', unit: 'm/s', min: 0.05, max: 5, step: 0.05 },
    { key: 'settleHoldSeconds', label: 'Pre-drop Settle', sub: 'Stabilize at hover height before lowering, and again once the rope is out before the hook opens. Added to the winch wait, which is computed', unit: 's', min: 0, max: 30, step: 1 },
    { key: 'postDropHoldSeconds', label: 'Post-drop Hold', sub: 'Extra hold after the rope is back in, then climb out', unit: 's', min: 0, max: 30, step: 1 },
    { key: 'winchArmProximityMeters', label: 'Winch Arm Range', sub: 'Horizontal distance to the drop point that arms the winch', unit: 'm', min: 1, max: 50, step: 0.5 },
  ],
  corridor: [
    { key: 'maxHorizontalDeviationMeters', label: 'Corridor Horizontal Half-Width', sub: 'Lateral tolerance — drawn tube AND enforced geofence', unit: 'm', min: 2, max: 100, step: 0.5 },
    { key: 'maxVerticalDeviationMeters', label: 'Corridor Vertical Half-Height', sub: 'Altitude tolerance the geofence enforces', unit: 'm', min: 1, max: 100, step: 0.5 },
    { key: 'timeBufferSeconds', label: 'Time Buffer', sub: 'Padding on each mission’s allocated time window', unit: 's', min: 0, max: 600, step: 5 },
    { key: 'safetyDistanceMeters', label: 'Separation Distance', sub: 'Horizontal separation two corridors must keep (3D when layers are off)', unit: 'm', min: 2, max: 200, step: 1 },
    { key: 'layerCeilingMeters', label: 'Layer Ceiling', sub: 'Top of the layered airspace, above home (121.92 m = 400 ft)', unit: 'm', min: 10, max: 500, step: 0.01 },
    { key: 'layerCount', label: 'Layer Count', sub: 'Equal bands below the ceiling; one band is the vertical separation', unit: '', min: 1, max: 50, step: 1 },
    { key: 'opacityActive', label: 'Active Corridor Opacity', sub: 'Transparency of the active/flying corridor tube', unit: '', min: 0, max: 1, step: 0.01 },
    { key: 'opacityPreview', label: 'Preview Corridor Opacity', sub: 'Transparency of the preview/saved corridor tube', unit: '', min: 0, max: 1, step: 0.01 },
  ],
  display: [
    { key: 'buildingClearanceMeters', label: 'Building Clearance', sub: 'Minimum clearance above building tops', unit: 'm', min: 0, max: 100, step: 1 },
    { key: 'droneScaleMeters', label: 'Drone Model Scale', sub: 'Rendered drone base size', unit: 'm', min: 0.2, max: 10, step: 0.1 },
    { key: 'trailMaxPoints', label: 'Trail Length', sub: 'Flight-trail history points', unit: 'pts', min: 10, max: 5000, step: 10 },
    { key: 'cameraBackDistanceMeters', label: 'Follow Cam Distance', sub: 'Distance behind the drone in follow view', unit: 'm', min: 2, max: 200, step: 1 },
  ],
};

/* Enum option lists (used for both the control and the Default column label). */
const OPT = {
  mapType: [{ id: 'satellite', label: 'Satellite' }, { id: 'street', label: 'Street' }, { id: 'terrain', label: 'Terrain' }],
  mapDimension: [{ id: '2d', label: '2D' }, { id: '3d', label: '3D' }],
  camera: [{ id: 'first_person', label: '1st' }, { id: 'second_person', label: '2nd' }, { id: 'third_person', label: '3rd' }],
  units: [{ id: 'metric', label: 'Metric' }, { id: 'imperial', label: 'Imperial' }],
  speed: [{ id: 'mps', label: 'm/s' }, { id: 'kmh', label: 'km/h' }, { id: 'knots', label: 'knots' }],
  altitude: [{ id: 'm', label: 'Meters' }, { id: 'ft', label: 'Feet' }],
  breach: [{ id: 'alert', label: 'Alert only' }, { id: 'hold', label: 'Hold' }, { id: 'rtl', label: 'RTL' }],
  batteryAction: [{ id: 'alert', label: 'Alert only' }, { id: 'rtl', label: 'RTL' }],
};

/* Defaults for the localStorage-backed UI fields (mirror the useState fallbacks). */
const UI_DEFAULTS = {
  mapType: 'satellite', mapDimension: '2d', cameraMode: 'third_person', defaultZoom: 12,
  show3DBuildings: true, showDroneTrails: true, geofenceAlerts: true,
  units: 'metric', speedUnit: 'mps', altitudeUnit: 'm',
  notifications: true, disconnectAlerts: true, batteryWarnings: true, breachAlerts: true,
};

const SWATCH_KEYS = ['bgPrimary', 'bgSecondary', 'accent', 'accentSecondary', 'textPrimary'];

/* ─── shared bits ─── */
function SvgIcon({ d, size = 18, color = 'currentColor' }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>;
}

function Panel({ panelRef, icon, title, desc, danger = false, flush = false, children }) {
  return (
    <section ref={panelRef} style={{
      scrollMarginTop: 14, marginBottom: 16,
      border: `1px solid ${danger ? 'color-mix(in srgb, var(--danger) 28%, transparent)' : 'var(--borderSecondary)'}`,
      borderRadius: 8, background: 'var(--bgSecondary)', overflow: 'hidden',
    }}>
      <header style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
        borderBottom: `1px solid ${danger ? 'color-mix(in srgb, var(--danger) 18%, transparent)' : 'var(--borderSecondary)'}`,
        background: danger ? 'color-mix(in srgb, var(--danger) 5%, transparent)' : 'var(--bgTertiary)',
      }}>
        <span style={{ color: danger ? '#f87171' : 'var(--accent)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <SvgIcon d={icon} size={15} />
        </span>
        <h3 style={{ margin: 0, fontFamily: FONT_MONO, fontSize: 12, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: danger ? '#f87171' : 'var(--textPrimary)' }}>{title}</h3>
      </header>
      <div style={{ padding: flush ? 0 : '4px 14px 10px' }}>
        {desc && <p style={{ fontFamily: FONT_BODY, fontSize: 12, color: 'var(--textMuted)', margin: 0, padding: flush ? '9px 14px 7px' : '9px 0 4px', lineHeight: 1.5 }}>{desc}</p>}
        {children}
      </div>
    </section>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} aria-pressed={value}
      style={{ width: 40, height: 21, borderRadius: 11, border: 'none', padding: 0, cursor: 'pointer',
        background: value ? 'var(--accent)' : 'var(--bgTertiary)', position: 'relative', flexShrink: 0, transition: 'background .2s' }}>
      <span style={{ position: 'absolute', top: 3, left: value ? 22 : 3, width: 15, height: 15, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.35)' }} />
    </button>
  );
}

function SegmentedControl({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', width: '100%', height: 28, borderRadius: 5, border: '1px solid var(--borderSecondary)', background: 'var(--bgPrimary)', overflow: 'hidden' }}>
      {options.map((opt, i) => (
        <button key={opt.id} onClick={() => onChange(opt.id)}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', cursor: 'pointer',
            fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', fontFamily: FONT_MONO, textTransform: 'uppercase', whiteSpace: 'nowrap',
            border: 'none', borderRight: i < options.length - 1 ? '1px solid var(--borderSecondary)' : 'none',
            background: value === opt.id ? 'var(--accent)' : 'transparent', color: value === opt.id ? 'var(--bgPrimary)' : 'var(--textMuted)', transition: 'all .15s' }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* Custom themed number field — clean stepper, native spinner suppressed. */
function NumberField({ value, min, max, step = 1, onChange }) {
  const clamp = (v) => {
    let r = v;
    if (typeof max === 'number') r = Math.min(max, r);
    if (typeof min === 'number') r = Math.max(min, r);
    return r;
  };
  const bump = (dir) => {
    const cur = parseFloat(value);
    const base = isNaN(cur) ? (typeof min === 'number' ? min : 0) : cur;
    onChange(String(clamp(Number((base + dir * step).toFixed(6)))));
  };
  return (
    <span className="mpcfg-numwrap">
      <input className="mpcfg-num" type="number" value={value} min={min} max={max} step={step} onChange={e => onChange(e.target.value)} />
      <span className="mpcfg-step">
        <button type="button" tabIndex={-1} onClick={() => bump(1)} aria-label="increment">▲</button>
        <button type="button" tabIndex={-1} onClick={() => bump(-1)} aria-label="decrement">▼</button>
      </span>
    </span>
  );
}

/* Renders the editable control for a param row, by field type. */
function ValueControl({ f }) {
  if (f.type === 'toggle') return (
    <span className="mpcfg-tgl">
      <Toggle value={!!f.value} onChange={f.set} />
      <span className="mpcfg-tgllbl">{f.value ? 'Enabled' : 'Disabled'}</span>
    </span>
  );
  if (f.type === 'segment') return <SegmentedControl options={f.options} value={f.value} onChange={f.set} />;
  if (f.type === 'color') return (
    <span className="mpcfg-color">
      <input type="color" value={f.value} onChange={e => f.set(e.target.value)} />
      <span className="mpcfg-hex">{String(f.value).toUpperCase()}</span>
    </span>
  );
  return <NumberField value={f.value} min={f.min} max={f.max} step={f.step || 1} onChange={f.set} />;
}

const fmtVal = (f, v) => {
  if (f.type === 'toggle') return v ? 'On' : 'Off';
  if (f.type === 'segment') return ((f.options.find(o => o.id === v) || {}).label) || String(v);
  if (f.type === 'color') return String(v).toUpperCase();
  return String(v);
};

/* Mission-Planner "Full Parameter List" style table for one section. */
function ParamTable({ rows }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="mpcfg">
        <colgroup>
          <col style={{ width: 208 }} />
          <col style={{ width: 220 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 62 }} />
          <col />
          <col style={{ width: 46 }} />
        </colgroup>
        <thead>
          <tr>
            <th>Name</th><th>Value</th><th className="th-def">Default</th><th className="th-unit">Units</th><th>Description</th><th className="th-act">↺</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(f => {
            const modified = String(f.value) !== String(f.baseline);
            const nonDefault = String(f.value) !== String(f.def);
            return (
              <tr key={f.key} className={modified ? 'mod' : ''}>
                <td className="c-name"><span className={'dot' + (modified ? ' on' : '')} />{f.name}</td>
                <td><ValueControl f={f} /></td>
                <td className="c-def">{fmtVal(f, f.def)}</td>
                <td className="c-unit">{f.unit || '—'}</td>
                <td className="c-desc">{f.desc}</td>
                <td className="c-act">
                  <button className="rst" onClick={() => f.set(f.def)} disabled={!nonDefault} title="Reset to default">
                    <SvgIcon d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Page ─── */
function SettingsPage({ isMobileLayout = false }) {
  const { theme, setTheme } = useTheme();
  const { logout } = useAuth();
  const mob = isMobileLayout;

  /* localStorage-backed UI state */
  const [mapType, setMapType] = useState(() => localStorage.getItem('utms_map_type') || 'satellite');
  const [mapDimension, setMapDimension] = useState(() => localStorage.getItem('utms_map_2d3d_mode') || '2d');
  const [cameraMode, setCameraMode] = useState(() => localStorage.getItem('utms_camera_mode') || 'third_person');
  const [show3DBuildings, setShow3DBuildings] = useState(() => localStorage.getItem('utms_3d_buildings') !== 'false');
  const [showDroneTrails, setShowDroneTrails] = useState(() => localStorage.getItem('utms_show_drone_trails') !== 'false');
  const [defaultZoom, setDefaultZoom] = useState(() => Number(localStorage.getItem('utms_default_zoom') || 12));
  const [geofenceAlerts, setGeofenceAlerts] = useState(() => localStorage.getItem('utms_geofence_alerts') !== 'false');
  const [notifications, setNotifications] = useState(() => localStorage.getItem('utms_notifications') !== 'false');
  const [disconnectAlerts, setDisconnectAlerts] = useState(() => localStorage.getItem('utms_disconnect_alerts') !== 'false');
  const [batteryWarnings, setBatteryWarnings] = useState(() => localStorage.getItem('utms_battery_warnings') !== 'false');
  const [breachAlerts, setBreachAlerts] = useState(() => localStorage.getItem('utms_breach_alerts') !== 'false');
  const [speedUnit, setSpeedUnit] = useState(() => localStorage.getItem('utms_speed_unit') || 'mps');
  const [altitudeUnit, setAltitudeUnit] = useState(() => localStorage.getItem('utms_altitude_unit') || 'm');
  const [units, setUnits] = useState(() => localStorage.getItem('utms_units') || 'metric');
  const [activeNav, setActiveNav] = useState('appearance');

  /* Search / filter (Mission Planner style) */
  const [search, setSearch] = useState('');
  const [onlyModified, setOnlyModified] = useState(false);
  const [onlyNonDefault, setOnlyNonDefault] = useState(false);

  /* Baseline snapshot of UI fields at load — powers the "Modified" filter. */
  const uiBase = useRef(null);
  if (uiBase.current === null) uiBase.current = {
    mapType, mapDimension, cameraMode, defaultZoom, show3DBuildings, showDroneTrails, geofenceAlerts,
    units, speedUnit, altitudeUnit, notifications, disconnectAlerts, batteryWarnings, breachAlerts,
  };

  /* Backend-backed ops config */
  const [ops, setOps] = useState(DEFAULT_OPS_CONFIG);
  const [opsBaseline, setOpsBaseline] = useState(DEFAULT_OPS_CONFIG);
  const [opsSaving, setOpsSaving] = useState(false);
  const [opsMsg, setOpsMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    loadOpsConfig().then((cfg) => { if (!alive) return; setOps(cfg); setOpsBaseline(cfg); });
    return () => { alive = false; };
  }, []);

  const opsDirty = JSON.stringify(ops) !== JSON.stringify(opsBaseline);

  const setOpsField = (group, key, raw) => {
    setOpsMsg(null);
    setOps(prev => ({ ...prev, [group]: { ...prev[group], [key]: raw } }));
  };

  const saveOps = async () => {
    setOpsSaving(true); setOpsMsg(null);
    const coerce = (group) => {
      const out = {};
      for (const [k, v] of Object.entries(ops[group])) {
        out[k] = typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) && typeof DEFAULT_OPS_CONFIG[group][k] === 'number' ? Number(v) : v;
      }
      return out;
    };
    try {
      const merged = await saveOpsConfig({ delivery: coerce('delivery'), dropTiming: coerce('dropTiming'), corridor: coerce('corridor'), display: coerce('display') });
      setOps(merged); setOpsBaseline(merged); setOpsMsg({ type: 'ok', text: 'Saved to server' });
    } catch (e) {
      setOpsMsg({ type: 'err', text: e.message || 'Save failed' });
    } finally { setOpsSaving(false); }
  };

  const resetOps = () => { setOps(opsBaseline); setOpsMsg(null); };

  const resetAllDefaults = () => {
    if (!window.confirm('Reset ALL settings on this page to their defaults? (Server config still needs Save to persist.)')) return;
    setMapType(UI_DEFAULTS.mapType); setMapDimension(UI_DEFAULTS.mapDimension); setCameraMode(UI_DEFAULTS.cameraMode);
    setDefaultZoom(UI_DEFAULTS.defaultZoom); setShow3DBuildings(UI_DEFAULTS.show3DBuildings); setShowDroneTrails(UI_DEFAULTS.showDroneTrails);
    setGeofenceAlerts(UI_DEFAULTS.geofenceAlerts); setUnits(UI_DEFAULTS.units); setSpeedUnit(UI_DEFAULTS.speedUnit); setAltitudeUnit(UI_DEFAULTS.altitudeUnit);
    setNotifications(UI_DEFAULTS.notifications); setDisconnectAlerts(UI_DEFAULTS.disconnectAlerts); setBatteryWarnings(UI_DEFAULTS.batteryWarnings); setBreachAlerts(UI_DEFAULTS.breachAlerts);
    setOps(JSON.parse(JSON.stringify(DEFAULT_OPS_CONFIG))); setOpsMsg(null);
  };

  /* Persist UI fields */
  useEffect(() => localStorage.setItem('utms_map_type', mapType), [mapType]);
  useEffect(() => localStorage.setItem('utms_map_2d3d_mode', mapDimension), [mapDimension]);
  useEffect(() => localStorage.setItem('utms_camera_mode', cameraMode), [cameraMode]);
  useEffect(() => localStorage.setItem('utms_3d_buildings', String(show3DBuildings)), [show3DBuildings]);
  useEffect(() => localStorage.setItem('utms_show_drone_trails', String(showDroneTrails)), [showDroneTrails]);
  useEffect(() => localStorage.setItem('utms_default_zoom', String(defaultZoom)), [defaultZoom]);
  useEffect(() => localStorage.setItem('utms_geofence_alerts', String(geofenceAlerts)), [geofenceAlerts]);
  useEffect(() => localStorage.setItem('utms_notifications', String(notifications)), [notifications]);
  useEffect(() => localStorage.setItem('utms_disconnect_alerts', String(disconnectAlerts)), [disconnectAlerts]);
  useEffect(() => localStorage.setItem('utms_battery_warnings', String(batteryWarnings)), [batteryWarnings]);
  useEffect(() => localStorage.setItem('utms_breach_alerts', String(breachAlerts)), [breachAlerts]);
  useEffect(() => localStorage.setItem('utms_speed_unit', speedUnit), [speedUnit]);
  useEffect(() => localStorage.setItem('utms_altitude_unit', altitudeUnit), [altitudeUnit]);
  useEffect(() => localStorage.setItem('utms_units', units), [units]);

  /* Scroll sync */
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});
  const setRef = (id) => (el) => { if (sectionRefs.current) sectionRefs.current[id] = el; };
  const scrollTo = useCallback((id) => { sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActiveNav(id); }, []);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handler = () => {
      const entries = SECTIONS.map(s => {
        const el = sectionRefs.current[s.id];
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: Math.abs(el.getBoundingClientRect().top - container.getBoundingClientRect().top) };
      });
      entries.sort((a, b) => a.top - b.top);
      if (entries[0]) setActiveNav(entries[0].id);
    };
    container.addEventListener('scroll', handler, { passive: true });
    return () => container.removeEventListener('scroll', handler);
  }, []);

  const handleClearLocalData = () => {
    ['utms_map_type','utms_map_2d3d_mode','utms_camera_mode','utms_3d_buildings','utms_units','utms_notifications','utms_show_drone_trails',
     'utms_default_zoom','utms_geofence_alerts','utms_disconnect_alerts','utms_battery_warnings','utms_breach_alerts','utms_speed_unit','utms_altitude_unit',
    ].forEach(k => localStorage.removeItem(k));
    window.location.reload();
  };

  /* ── Build the param-row descriptors (value/default/baseline/setter per field) ── */
  const opsF = (group, f) => ({ key: `${group}.${f.key}`, name: f.label, desc: f.sub, unit: f.unit, type: 'number', min: f.min, max: f.max, step: f.step,
    value: ops[group][f.key], def: DEFAULT_OPS_CONFIG[group][f.key], baseline: opsBaseline[group][f.key], set: v => setOpsField(group, f.key, v) });
  const opsX = (group, key, name, desc, type, options) => ({ key: `${group}.${key}`, name, desc, unit: '', type, options,
    value: ops[group][key], def: DEFAULT_OPS_CONFIG[group][key], baseline: opsBaseline[group][key], set: v => setOpsField(group, key, v) });
  const uiF = (key, name, desc, type, options, value, set, extra = {}) => ({ key, name, desc, unit: extra.unit || '', type, options,
    min: extra.min, max: extra.max, step: extra.step, value, def: UI_DEFAULTS[key], baseline: uiBase.current[key], set });

  const sectionData = [
    { id: 'map', title: 'Map Settings', fields: [
      uiF('mapType', 'Map Type', 'Default map layer', 'segment', OPT.mapType, mapType, setMapType),
      uiF('mapDimension', 'Map Dimension', 'Default map mode in the Map section', 'segment', OPT.mapDimension, mapDimension, setMapDimension),
      uiF('cameraMode', 'Default 3D Camera', 'Camera perspective used when 3D mode opens', 'segment', OPT.camera, cameraMode, setCameraMode),
      uiF('defaultZoom', 'Default Zoom', 'Initial zoom level when map loads', 'number', null, defaultZoom, v => setDefaultZoom(Number(v)), { min: 4, max: 22, step: 1 }),
      uiF('show3DBuildings', '3D Buildings', 'Render extruded buildings and terrain effects in 3D mode', 'toggle', null, show3DBuildings, setShow3DBuildings),
      uiF('showDroneTrails', 'Drone Trails', 'Show flight path history on map', 'toggle', null, showDroneTrails, setShowDroneTrails),
      uiF('geofenceAlerts', 'Geofence Alerts', 'Show boundary and alert zones', 'toggle', null, geofenceAlerts, setGeofenceAlerts),
    ] },
    { id: 'delivery', title: 'Delivery & Winch', desc: 'Defaults used when generating a winch-delivery mission. Shared across all operators.', fields: OPS_FIELDS.delivery.map(f => opsF('delivery', f)) },
    { id: 'dropTiming', title: 'Drop Timing', desc: 'Controls when the parcel drop begins and how soon the drone heads home — the drone must reach and settle at drop height before the winch runs.', fields: OPS_FIELDS.dropTiming.map(f => opsF('dropTiming', f)) },
    { id: 'airspace', title: 'Airspace & Corridors', desc: 'Air-corridor geometry and 4D deconfliction (space + allocated time window).', fields: [
      ...OPS_FIELDS.corridor.map(f => opsF('corridor', f)),
      opsX('corridor', 'deconflictEnabled', '4D Deconfliction', "Block a new mission whose corridor overlaps another drone's in space AND time", 'toggle'),
      opsX('corridor', 'layersEnabled', 'Altitude Layers', 'Separate flights by layer: different layers never conflict, and a conflicting route moves to the nearest free layer', 'toggle'),
      opsX('corridor', 'breachAction', 'Corridor Breach Action', 'What a flying drone does when it leaves its 3D corridor', 'segment', OPT.breach),
      opsX('corridor', 'colorCruise', 'Cruise Color', 'Corridor cruise segment', 'color'),
      opsX('corridor', 'colorTakeoff', 'Takeoff Color', 'Climb-out segment', 'color'),
      opsX('corridor', 'colorLand', 'Landing Color', 'Descent segment', 'color'),
    ] },
    { id: 'safety', title: 'Safety Actions', desc: 'What the backend does automatically when a drone hits a safety threshold mid-mission. Shared across all operators.', fields: [
      opsX('safety', 'batteryCriticalAction', 'Critical Battery Action', "Below the drone's battery RTL threshold while a mission is running", 'segment', OPT.batteryAction),
    ] },
    { id: 'tuning3d', title: '3D Display Tuning', fields: OPS_FIELDS.display.map(f => opsF('display', f)) },
    { id: 'units', title: 'Units & Telemetry', fields: [
      uiF('units', 'Measurement System', 'Metric or Imperial units', 'segment', OPT.units, units, setUnits),
      uiF('speedUnit', 'Speed Unit', 'Displayed on telemetry', 'segment', OPT.speed, speedUnit, setSpeedUnit),
      uiF('altitudeUnit', 'Altitude Unit', 'Altitude display format', 'segment', OPT.altitude, altitudeUnit, setAltitudeUnit),
    ] },
    { id: 'notifications', title: 'Alerts', fields: [
      uiF('notifications', 'Mission Alerts', 'Receive alerts on mission status changes', 'toggle', null, notifications, setNotifications),
      uiF('disconnectAlerts', 'Drone Disconnect', 'Alert when a drone loses connection', 'toggle', null, disconnectAlerts, setDisconnectAlerts),
      uiF('batteryWarnings', 'Battery Warnings', 'Low battery level notifications', 'toggle', null, batteryWarnings, setBatteryWarnings),
      uiF('breachAlerts', 'Geofence Breach', 'Alert on geofence violation', 'toggle', null, breachAlerts, setBreachAlerts),
    ] },
  ];

  const allFields = sectionData.flatMap(s => s.fields);
  const modifiedCount = allFields.filter(f => String(f.value) !== String(f.baseline)).length;
  const nonDefaultCount = allFields.filter(f => String(f.value) !== String(f.def)).length;

  const q = search.trim().toLowerCase();
  const filterActive = q !== '' || onlyModified || onlyNonDefault;
  const matchField = (f) => {
    if (q && !(f.name.toLowerCase().includes(q) || (f.desc || '').toLowerCase().includes(q) || f.key.toLowerCase().includes(q))) return false;
    if (onlyModified && String(f.value) === String(f.baseline)) return false;
    if (onlyNonDefault && String(f.value) === String(f.def)) return false;
    return true;
  };

  /* Nav button */
  const navBtn = (active) => ({
    display: 'flex', alignItems: 'center', gap: 9, padding: mob ? '7px 10px' : '8px 12px', borderRadius: 6, cursor: 'pointer',
    fontFamily: FONT_MONO, fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', width: '100%', textAlign: 'left', border: 'none',
    background: active ? 'rgba(34,211,238,.07)' : 'transparent', color: active ? 'var(--accent)' : 'var(--textMuted)',
    borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent', transition: 'all .15s', whiteSpace: 'nowrap',
  });

  const actionBtn = (primary, enabled) => ({
    width: '100%', padding: mob ? '10px 6px' : '8px 12px', borderRadius: 5, fontFamily: FONT_MONO, fontSize: mob ? 10.5 : 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
    cursor: enabled ? 'pointer' : 'default', textAlign: mob ? 'center' : 'left', margin: mob ? 0 : undefined, flex: mob ? 1 : undefined,
    border: primary ? 'none' : '1px solid var(--borderSecondary)', background: primary ? 'var(--accent)' : 'transparent',
    color: primary ? 'var(--bgPrimary)' : 'var(--textSecondary)', opacity: enabled ? 1 : 0.45, transition: 'all .15s',
  });

  return (
    <div className="mpcfg-page" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bgPrimary)', overflow: 'hidden', fontFamily: FONT_BODY }}>
      <style>{PARAM_CSS}</style>
      {/* ── Header ── */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: mob ? '0 16px' : '0 24px', height: 50, borderBottom: '1px solid var(--borderSecondary)', background: 'var(--bgSecondary)' }}>
        <span style={{ color: 'var(--accent)', display: 'grid', placeItems: 'center' }}>
          <SvgIcon d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" size={18} />
        </span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 14, fontWeight: 700, color: 'var(--textPrimary)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Settings</span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: 'var(--textMuted)', letterSpacing: '.1em', border: '1px solid var(--borderSecondary)', borderRadius: 4, padding: '2px 7px', textTransform: 'uppercase' }}>Config List</span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: 'var(--textMuted)', marginLeft: 'auto' }}>BERAM UTMS {APP_VERSION}</span>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: mob ? 'column' : 'row' }}>

        {/* Left nav */}
        <nav style={{ flexShrink: 0, width: mob ? '100%' : 196, borderRight: mob ? 'none' : '1px solid var(--borderSecondary)', borderBottom: mob ? '1px solid var(--borderSecondary)' : 'none',
          background: 'var(--bgSecondary)', padding: mob ? '8px' : '14px 8px', display: 'flex', flexDirection: mob ? 'row' : 'column', gap: 2, overflow: mob ? 'auto' : undefined }}>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => scrollTo(s.id)} style={navBtn(activeNav === s.id)}>
              <SvgIcon d={s.icon} size={15} />
              <span>{s.label}</span>
            </button>
          ))}
        </nav>

        {/* Center: tables */}
        <main ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: mob ? '14px 12px 24px' : '16px 20px 48px' }}>
          {/* Phone: search sits on top of the list (the side column is desktop-only) */}
          {mob && (
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parameters…"
              style={{ width: '100%', margin: '0 0 12px', padding: '9px 12px', borderRadius: 8, fontFamily: FONT_MONO, border: '1px solid var(--borderSecondary)', background: 'var(--bgSecondary)', color: 'var(--textPrimary)', outline: 'none', boxSizing: 'border-box' }} />
          )}

          {/* Appearance (special, hidden while filtering) */}
          {!filterActive && (
            <Panel panelRef={setRef('appearance')} icon={iconOf('appearance')} title="Appearance" desc="Color theme for the interface.">
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: mob ? '1fr' : 'repeat(auto-fill, minmax(180px, 1fr))', paddingTop: 4 }}>
                {Object.entries(THEMES).map(([key, td]) => (
                  <button key={key} onClick={() => setTheme(key)}
                    style={{ padding: 14, borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                      border: theme === key ? '1.5px solid var(--accent)' : '1px solid var(--borderSecondary)',
                      background: theme === key ? 'rgba(34,211,238,.05)' : 'var(--bgPrimary)', transition: 'all .2s', position: 'relative' }}>
                    {theme === key && (
                      <div style={{ position: 'absolute', top: 10, right: 10, width: 17, height: 17, borderRadius: '50%', background: 'var(--accent)', display: 'grid', placeItems: 'center' }}>
                        <svg width={10} height={10} viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--bgPrimary)' }} strokeWidth={3}><path d="M5 13l4 4L19 7" /></svg>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                      {SWATCH_KEYS.map(ck => (<span key={ck} style={{ width: 20, height: 20, borderRadius: 4, background: td.colors[ck], border: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)' }} />))}
                    </div>
                    <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: theme === key ? 'var(--accent)' : 'var(--textPrimary)' }}>{td.name}</div>
                    <div style={{ fontFamily: FONT_BODY, fontSize: 11, color: 'var(--textMuted)', marginTop: 3 }}>{key === 'default' ? 'Navy slate palette' : key === 'dark' ? 'Pure dark mode' : 'Light interface'}</div>
                  </button>
                ))}
              </div>
            </Panel>
          )}

          {/* Param tables */}
          {sectionData.map(s => {
            const rows = s.fields.filter(matchField);
            if (rows.length === 0) return null;
            return (
              <Panel key={s.id} panelRef={setRef(s.id)} icon={iconOf(s.id)} title={s.title} desc={s.desc} flush>
                <ParamTable rows={rows} />
              </Panel>
            );
          })}

          {filterActive && sectionData.every(s => s.fields.filter(matchField).length === 0) && (
            <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: 'var(--textMuted)', padding: '24px 4px', textAlign: 'center' }}>No parameters match the current filter.</div>
          )}

          {/* About + Danger (special, hidden while filtering) */}
          {!filterActive && (
            <Panel panelRef={setRef('about')} icon={iconOf('about')} title="About">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 2px' }}>
                <img src={`${process.env.PUBLIC_URL}/BERAMLOGO.png`} alt="BERAM" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'contain' }} />
                <div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 15, fontWeight: 700, letterSpacing: '.04em', color: 'var(--textPrimary)' }}>BERAM UTMS</div>
                  <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: 'var(--textMuted)', marginTop: 2 }}>Unmanned Traffic Management System</div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: 'var(--textMuted)', marginTop: 5, display: 'flex', gap: 12, letterSpacing: '.05em' }}>
                    <span>{APP_VERSION.toUpperCase()}</span><span>© 2026 BERAM</span>
                  </div>
                </div>
              </div>
            </Panel>
          )}

          {!filterActive && (
            <Panel panelRef={setRef('danger')} icon={iconOf('danger')} title="Danger Zone" danger>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 10 }}>
                {[
                  { onClick: handleClearLocalData, icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', title: 'Reset All Preferences', sub: 'Clears all local settings and reloads the application' },
                  { onClick: logout, icon: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1', title: 'Sign Out', sub: 'End your current session' },
                ].map((b, i) => (
                  <button key={i} onClick={b.onClick}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '13px 14px', cursor: 'pointer', textAlign: 'left', borderRadius: 6,
                      border: '1px solid color-mix(in srgb, var(--danger) 22%, transparent)', background: 'color-mix(in srgb, var(--danger) 4%, transparent)' }}>
                    <div style={{ width: 34, height: 34, borderRadius: 7, background: 'color-mix(in srgb, var(--danger) 8%, transparent)', display: 'grid', placeItems: 'center', flexShrink: 0, color: '#f87171' }}>
                      <SvgIcon d={b.icon} size={16} />
                    </div>
                    <div>
                      <div style={{ fontFamily: FONT_MONO, fontSize: 12.5, fontWeight: 700, letterSpacing: '.03em', color: '#f87171' }}>{b.title}</div>
                      <div style={{ fontFamily: FONT_BODY, fontSize: 11, color: 'var(--textMuted)', marginTop: 2 }}>{b.sub}</div>
                    </div>
                  </button>
                ))}
              </div>
            </Panel>
          )}
        </main>

        {/* Right action column (Mission Planner style). Phone: a one-row action bar under the list. */}
        <aside style={{ flexShrink: 0, width: mob ? '100%' : 210, borderLeft: mob ? 'none' : '1px solid var(--borderSecondary)', borderTop: mob ? '1px solid var(--borderSecondary)' : 'none',
          background: 'var(--bgSecondary)', padding: mob ? '10px 12px' : 14, display: 'flex', flexDirection: 'column', gap: mob ? 6 : 8, overflow: mob ? undefined : 'auto' }}>

          <div style={{ display: 'flex', flexDirection: mob ? 'row' : 'column', gap: 6 }}>
            <button onClick={saveOps} disabled={!opsDirty || opsSaving} style={actionBtn(true, opsDirty && !opsSaving)}>{opsSaving ? 'Saving…' : 'Write Params'}</button>
            <button onClick={resetOps} disabled={!opsDirty || opsSaving} style={actionBtn(false, opsDirty && !opsSaving)}>Revert Unsaved</button>
            <button onClick={resetAllDefaults} style={actionBtn(false, true)}>Reset to Default</button>
          </div>

          {opsMsg && (
            <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: opsMsg.type === 'ok' ? '#34d399' : '#f87171', letterSpacing: '.02em' }}>{opsMsg.text}</div>
          )}
          {opsDirty && <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: 'var(--warning, var(--warning))', letterSpacing: '.02em' }}>Server config not saved</div>}

          {!mob && (<>
          <div style={{ height: 1, background: 'var(--borderSecondary)', margin: '4px 0' }} />

          {/* Search */}
          <label style={{ fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--textMuted)' }}>Search</label>
          <div style={{ position: 'relative' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="filter by name…"
              style={{ width: '100%', margin: 0, padding: '7px 26px 7px 9px', borderRadius: 5, fontFamily: FONT_MONO, fontSize: 12, border: '1px solid var(--borderSecondary)', background: 'var(--bgPrimary)', color: 'var(--textPrimary)', outline: 'none', boxSizing: 'border-box' }} />
            {search && (
              <button onClick={() => setSearch('')} title="Clear" style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: 'var(--textMuted)', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
            )}
          </div>

          {/* Filters */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--textSecondary)', marginTop: 4 }}>
            <input type="checkbox" checked={onlyModified} onChange={e => setOnlyModified(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
            Modified <span style={{ color: 'var(--textMuted)' }}>({modifiedCount})</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--textSecondary)' }}>
            <input type="checkbox" checked={onlyNonDefault} onChange={e => setOnlyNonDefault(e.target.checked)} style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
            Non-default <span style={{ color: 'var(--textMuted)' }}>({nonDefaultCount})</span>
          </label>

          <div style={{ marginTop: 'auto', fontFamily: FONT_MONO, fontSize: 10, color: 'var(--textMuted)', lineHeight: 1.6, letterSpacing: '.02em' }}>
            {allFields.length} params · {sectionData.length} groups
          </div>
          </>)}
        </aside>
      </div>
    </div>
  );
}

export default SettingsPage;
